import type { WatchQueryFetchPolicy } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import { expVal } from '@confluence/feature-experiments';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { FetchPolicy } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

import type { SitePersonalizationQuery_experimentalSitePersonalization_attributes as SitePersonalizationAttributes } from './__types__/SitePersonalizationQuery.ts';
// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- Required for legacy dynamic behavior while cc_ce_mau_deprecated_tap_wrappers_migration rolls out. See ACTIVATION-2527
import { useExperimentalSitePersonalizationQuery } from './useExperimentalSitePersonalizationQuery.ts';

export type useSitePersonalizationQueryProps = {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
};

export let skipQueryIfUnauthorized = false;

export const useSitePersonalizationQuery = ({
	cloudId,
	skip,
	fetchPolicy,
}: {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
}) => {
	const isLicensed = useIsLicensed();

	const skipOptions =
		skip ||
		!isLicensed ||
		skipQueryIfUnauthorized ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR); // SitePersonalizationQuery isn't preloaded and can be skipped during SSR to avoid unnecessary requests

	const legacyQuery = useExperimentalSitePersonalizationQuery({
		variables: {
			cloudId,
		},
		fetchPolicy: (fetchPolicy || 'cache-first') as FetchPolicy,
		skip:
			skipOptions ||
			expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
		onError(apolloError: ApolloErrorInterop) {
			skipQueryIfUnauthorized = apolloError?.graphQLErrors?.some(
				(graphQLError: GraphQLError) =>
					isStatusCodeError(graphQLError, '401') || isUnauthorizedError(graphQLError),
			);
		},
	});
	const siteTraits = useSiteTraits({
		skipBootstrap:
			skipOptions ||
			!expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
	});

	if (skipOptions) {
		return {
			loading: false,
			error: undefined,
			attributes: [],
		};
	}

	// The experiment path reads snapshot-backed site traits and does not guarantee
	// same-session freshness. Keep the legacy query path available while the
	// migration rolls out for callers that still need dynamic TAP behavior.
	const attributes: SitePersonalizationAttributes[] =
		(expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)
			? siteTraits.data?.attributes
			: legacyQuery.data?.experimentalSitePersonalization?.attributes) || [];
	return {
		loading: expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)
			? siteTraits.loading
			: legacyQuery.loading,
		error: expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)
			? (siteTraits.error ?? undefined)
			: legacyQuery.error,
		attributes,
	};
};
